/* import __COLOCATED_TEMPLATE__ from './if-branch.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { type default as GraphEditorNode } from 'graph-editor/models/graph-editor/node';

import type ConnectionPoint from 'embercom/models/operator/visual-builder/connection-point';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type Group from 'embercom/models/operator/visual-builder/group';
import type ConditionalBranchesConfiguration from 'embercom/objects/visual-builder/configuration/step/conditional-branches';
import type EditorConfig from 'embercom/objects/visual-builder/configuration/editor';
import type IntlService from 'embercom/services/intl';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';

type IfBranchArgs = {
  branch: ConnectionPoint;
  editorState: EditorState;
  readOnly: boolean;
  index: number;
  updateConnectionPoints: (node: GraphEditorNode<Group>) => void;
  connectionPointComponent: Component;
  stepConfig: ConditionalBranchesConfiguration;
  editorConfiguration: EditorConfig;
  sortableGroupName: string;
};

export default class IfBranch extends Component<IfBranchArgs> {
  @service declare intl: IntlService;
  @service declare appService: any;
  @service declare aiContentSegmentsService: AiContentSegmentsService;

  constructor(owner: unknown, args: IfBranchArgs) {
    super(owner, args);

    this.args.branch.predicateGroup.set('attributeListOverride', this.attributeListOverride);

    if (this.canUseAudienceBranching && !this.aiContentSegmentsService.hasLoadedSegments) {
      this.aiContentSegmentsService.loadSegments();
    }
  }

  get graph() {
    return this.args.editorState.layout.graph;
  }

  private get audienceAttribute() {
    return {
      identifier: 'audience_id',
      name: this.intl.t(
        'operator.workflows.visual-builder.conditional-branches.audience-attribute-name',
      ),
      type: 'audience',
      icon: 'multiple-people',
    };
  }

  private get canUseAudienceBranching(): boolean {
    return this.appService.app.canUseFeature('workflows-audience-branching');
  }

  get attributeGroupList() {
    let base = this.args.editorConfiguration.runtimeMatchingTargetingAttributes;
    if (!this.canUseAudienceBranching || !base.length) {
      return base;
    }

    let [firstGroup, ...rest] = base as any[];
    return [
      { ...firstGroup, attributes: [this.audienceAttribute, ...firstGroup.attributes] },
      ...rest,
    ];
  }

  get attributeListOverride() {
    let base = this.args.editorConfiguration.allRuntimeMatchingTargetingAttributes
      .map((group) => group.attributes)
      .flat();

    if (this.canUseAudienceBranching) {
      return [this.audienceAttribute, ...base];
    }
    return base;
  }

  get supportedPredicateAttributes() {
    return this.args.editorConfiguration.runtimeMatchingTargetingAttributeIdentifiers;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::ConditionalBranches::IfBranch': typeof IfBranch;
    'workflows/graph-editor/node-items/steps/conditional-branches/if-branch': typeof IfBranch;
  }
}
